import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import {
  Operatory,
  OperatoryDetails,
  OperatoryDisplayNameUpdate,
  OperatoryLocationAssignmentUpdate,
} from '@weave/schema-gen-ts/dist/schemas/schedule/v3/operatory.pb';
import { SortOrder } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/sort.pb';
import { DataSource } from '@weave/schema-gen-ts/dist/schemas/syncapp/integrations-service/datasource.pb';
import { DataSourcesQueries } from '@frontend/api-data-sources';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { useTranslationTokens } from '@frontend/i18n-tokens';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Table, TableColumnConfig, TableRowActions, useAlert, Chip } from '@frontend/design-system';
import { useDataSourceColumn } from '../../../hooks/use-data-source-column';
import { useSchedulingLocationInfo } from '../../../hooks/use-scheduling-location-info';
import { isOpertoriesV3SortByAvailable, OPERTORIES_V3_SORT_BY, PageConfig } from '../../../types';
import { getOperatoryAvatarInfoByName, OperatoryAvatarInfo } from '../helpers';
import { OperatoryAvatar } from '../operatory-avatar';
import { EditCell, InlineEditAction } from './inline-fields';
import { TableCustomFilters } from './table-custom-filters';
import { TableHeader } from './table-header';
import { TableFilters } from '.';

type Props = {
  pageConfig: PageConfig;
  operatoriesData: Operatory[];
  dataSourcesData: DataSource[];
  totalCount: number;
  isLoading: boolean;
  setPageConfig: Dispatch<SetStateAction<PageConfig>>;
  refetchOperatoryData: () => void;
  navigateTo: (operatoryId: string) => void;
  setFilters: (filters: TableFilters) => void;
};

export type EditingRowState = {
  operatoryId: string;
  displayName: string;
  hidden: boolean;
  details: OperatoryDetails;
  locationId: string;
  avatarInfo: OperatoryAvatarInfo;
};

export type EditingColState = {
  colId: string;
  editingData: Map<string, Partial<EditingRowState>>;
};

export const UNASSIGNED_LABEL = 'Unassigned';

export const ManageOperatoryTable = ({
  pageConfig,
  isLoading,
  operatoriesData,
  dataSourcesData,
  setPageConfig,
  totalCount,
  refetchOperatoryData,
  setFilters,
}: Props) => {
  const { t } = useTranslation('schedule-settings');
  const alert = useAlert();
  const tokens = useTranslationTokens();
  const { isMultiLocation, getLocationName, selectedLocationIds, parentLocationId } = useSchedulingLocationInfo();
  const effectiveParentLocationId = isMultiLocation ? parentLocationId : undefined;
  const { isIntegratedOffice } = DataSourcesQueries.useGetAccessibleIntegrations();
  const updateOperatoryMutation = SchedulerV3.Mutations.useUpdateOperatoryMutation();

  const [editingRowData, setEditingRowData] = useState<EditingRowState | null>(null);
  const [editingColData, setEditingColData] = useState<EditingColState | null>(null);
  const editingRowId = editingRowData?.operatoryId;
  const editingColId = editingColData?.colId;

  const { shouldShowColumn: showSourceColumn } = useDataSourceColumn<Operatory>({
    sourceIdField: 'sourceId',
  });

  const handleColumnEdit = useCallback(
    (colId: string) => {
      const editingData = new Map<string, Partial<EditingRowState>>();

      // Initialize editing data for all operatories
      operatoriesData.forEach((operatory) => {
        const avatarInfo = getOperatoryAvatarInfoByName(
          operatory.externalName || operatory.displayName || '',
          operatory.details?.icon
        );

        editingData.set(operatory.id, {
          operatoryId: operatory.id,
          displayName: operatory.displayName ?? operatory.externalName ?? '',
          hidden: operatory.hidden ?? true,
          details: operatory.details ?? {},
          locationId: operatory.locationId ?? '',
          avatarInfo,
        });
      });

      setEditingColData({ colId, editingData });
    },
    [operatoriesData]
  );

  const handleRowEdit = useCallback((operatory: Operatory) => {
    const avatarInfo = getOperatoryAvatarInfoByName(
      operatory.externalName || operatory.displayName || '',
      operatory.details?.icon
    );

    setEditingRowData({
      operatoryId: operatory.id,
      displayName: operatory.displayName ?? operatory.externalName ?? '',
      hidden: operatory.hidden ?? true,
      details: operatory.details ?? {},
      locationId: operatory.locationId ?? '',
      avatarInfo,
    });
  }, []);

  const handleCancelColumnEdit = useCallback(() => {
    setEditingColData(null);
  }, []);

  const handleCancelRowEdit = useCallback(() => {
    setEditingRowData(null);
  }, []);

  const updateColumnEditData = useCallback((operatoryId: string, field: string, value: any) => {
    setEditingColData((prev) => {
      if (!prev) return null;
      const newEditingData = new Map(prev.editingData);
      const operatoryData = newEditingData.get(operatoryId) || {};
      newEditingData.set(operatoryId, { ...operatoryData, [field]: value });
      return { ...prev, editingData: newEditingData };
    });
  }, []);

  const handleSaveRowEdit = useCallback(async () => {
    if (!editingRowData) return;
    const isUnassignedLocation = isMultiLocation && editingRowData.locationId === parentLocationId;

    try {
      await updateOperatoryMutation.mutateAsync({
        operatoryId: editingRowData.operatoryId,
        displayName: editingRowData.displayName,
        hidden: isUnassignedLocation ? true : editingRowData.hidden,
        details: {
          ...editingRowData.details,
          icon: {
            color: editingRowData.avatarInfo.color,
            name: editingRowData.avatarInfo.icon,
          },
        },
        childLocationId: isMultiLocation ? editingRowData.locationId : undefined,
      });

      alert.success(
        t('{{operatory}} details updated successfully', {
          operatory: tokens.operatory,
        })
      );

      handleCancelRowEdit();
      refetchOperatoryData();
    } catch {
      alert.error(
        t('Failed to update {{operatory}} details', {
          operatory: tokens.operatory,
        })
      );
    }
  }, [
    editingRowData,
    updateOperatoryMutation,
    isMultiLocation,
    parentLocationId,
    alert,
    t,
    tokens,
    handleCancelRowEdit,
    refetchOperatoryData,
  ]);

  const bulkUpdateOperatoriesMutation = SchedulerV3.Mutations.useBulkUpdateOperatoriesMutation();

  const handleSaveDisplayNamesColumnEdit = useCallback(async () => {
    if (!editingColData?.editingData) return;

    try {
      const operatoriesById = new Map(operatoriesData.map((op) => [op.id, op]));
      const displayNameUpdates: OperatoryDisplayNameUpdate[] = [];

      editingColData.editingData.forEach((editData, operatoryId) => {
        const originalOperatory = operatoriesById.get(operatoryId);
        if (originalOperatory && editData.displayName && editData.displayName !== originalOperatory.displayName) {
          displayNameUpdates.push({
            operatoryId: originalOperatory.id,
            displayName: editData.displayName,
            childLocationId: isMultiLocation ? originalOperatory.locationId || '' : undefined,
          });
        }
      });

      if (displayNameUpdates.length === 0) {
        alert.info(t('No display name changes to save'));
        return;
      }

      await bulkUpdateOperatoriesMutation.mutateAsync({
        locationId: parentLocationId,
        operationType: 'DISPLAY_NAME',
        displayNameUpdate: { updates: displayNameUpdates },
      });

      alert.success(t('Display names updated successfully'));
      handleCancelColumnEdit();
      refetchOperatoryData();
    } catch (error) {
      console.error('Error bulk updating operatories display names:', error);
      alert.error(t('Failed to update display names'));
    }
  }, [
    alert,
    bulkUpdateOperatoriesMutation,
    editingColData?.editingData,
    handleCancelColumnEdit,
    isMultiLocation,
    operatoriesData,
    parentLocationId,
    refetchOperatoryData,
    t,
  ]);

  const handleSaveLocationsColumnEdit = useCallback(async () => {
    if (!editingColData?.editingData) return;

    try {
      const operatoriesById = new Map(operatoriesData.map((op) => [op.id, op]));
      const locationUpdates: OperatoryLocationAssignmentUpdate[] = [];

      editingColData.editingData.forEach((editData, operatoryId) => {
        const originalOperatory = operatoriesById.get(operatoryId);
        if (originalOperatory && editData.locationId !== originalOperatory.locationId) {
          locationUpdates.push({
            operatoryId: originalOperatory.id,
            childLocationId: editData.locationId === parentLocationId ? undefined : editData.locationId,
          });
        }
      });

      if (locationUpdates.length === 0) {
        alert.info(t('No location changes to save'));
        return;
      }

      await bulkUpdateOperatoriesMutation.mutateAsync({
        locationId: parentLocationId,
        operationType: 'LOCATION_ASSIGNMENT',
        locationAssignmentUpdate: { updates: locationUpdates },
      });

      alert.success(t('Locations updated successfully'));
      handleCancelColumnEdit();
      refetchOperatoryData();
    } catch (error) {
      console.error('Error bulk updating operatories locations:', error);
      alert.error(t('Failed to update locations'));
    }
  }, [
    alert,
    bulkUpdateOperatoriesMutation,
    editingColData?.editingData,
    handleCancelColumnEdit,
    operatoriesData,
    parentLocationId,
    refetchOperatoryData,
    t,
  ]);

  const handleOnArchive = useCallback(
    (operatory: Operatory) => {
      updateOperatoryMutation
        .mutateAsync({
          operatoryId: operatory.id,
          hidden: true,
          details: {
            ...operatory.details,
            act: !operatory.details?.act,
          },
          childLocationId: isMultiLocation ? operatory?.locationId || '' : undefined,
        })
        .then((res) => {
          if (res) {
            alert.success(
              t('{{operatory}} {{status}} successfully', {
                operatory: tokens.operatory,
                status: !!operatory.details?.act ? 'archived' : 'unarchived',
              })
            );
            refetchOperatoryData();
          }
        })
        .catch(() => {
          alert.error(
            t('Failed to {{status}} {{operatory}}', {
              status: !!operatory.details?.act ? 'archive' : 'unarchive',
              operatory: tokens.operatory,
            })
          );
        });
    },
    [alert, isMultiLocation, refetchOperatoryData, t, tokens, updateOperatoryMutation]
  );

  // Get list of locations for dropdown
  const locationOptions = useMemo(() => {
    return selectedLocationIds
      .map((id) => ({
        value: id,
        label: getLocationName(id),
      }))
      .concat({
        value: isMultiLocation ? parentLocationId : 'unassigned',
        label: t(`{{unassignedLabel}}`, { unassignedLabel: UNASSIGNED_LABEL }),
      });
  }, [selectedLocationIds, getLocationName, t, isMultiLocation, parentLocationId]);

  const tableColumns = useMemo(
    (): TableColumnConfig<Operatory>[] => [
      {
        Header: t('Icon'),
        id: 'Icon',
        disableSortBy: true,
        maxWidth: 100,
        accessor: (row) => row,
        cellRenderer: (row) =>
          editingRowId === row.id ? (
            <EditCell
              colId='Icon'
              editingRowData={editingRowData}
              setEditingRowData={setEditingRowData}
              parentLocationId={effectiveParentLocationId}
            />
          ) : (
            <OperatoryAvatar
              avatarInfo={row.details?.icon}
              operatoryName={row.displayName || row.externalName || ''}
              disabled={!row.details?.act}
            />
          ),
      },
      {
        Header: (
          <TableHeader
            colId='DisplayName'
            isColEditing={editingColId === 'DisplayName'}
            isRowEditing={!!editingRowId}
            onEdit={handleColumnEdit}
            onSave={handleSaveDisplayNamesColumnEdit}
            onCancel={handleCancelColumnEdit}
          />
        ),
        id: 'DisplayName',
        disableSortBy: true,
        minWidth: 220,
        accessor: (row) => row,
        cellRenderer: (row) => {
          const isColumnEditing = editingColId === 'DisplayName';
          const isRowEditing = editingRowId === row.id;

          if (isColumnEditing) {
            const columnEditData = editingColData?.editingData.get(row.id);
            return (
              <EditCell
                colId='DisplayName'
                editingRowData={columnEditData as EditingRowState}
                setEditingRowData={(updatedData) => {
                  if (typeof updatedData === 'function') return;
                  if (updatedData?.displayName) {
                    updateColumnEditData(row.id, 'displayName', updatedData.displayName);
                  }
                }}
                parentLocationId={effectiveParentLocationId}
              />
            );
          }

          if (isRowEditing) {
            return (
              <EditCell
                colId='DisplayName'
                editingRowData={editingRowData}
                setEditingRowData={setEditingRowData}
                parentLocationId={effectiveParentLocationId}
              />
            );
          }

          return row.displayName || row.externalName || '-';
        },
      },
      {
        Header: t('System Name'),
        id: 'ExternalName',
        disableSortBy: true,
        minWidth: 200,
        accessor: (row) => row,
        cellRenderer: (row) => row.externalName || '-',
      },
      {
        Header: (
          <TableHeader
            colId='LocationId'
            isColEditing={editingColId === 'LocationId'}
            isRowEditing={!!editingRowId}
            onEdit={handleColumnEdit}
            onSave={handleSaveLocationsColumnEdit}
            onCancel={handleCancelColumnEdit}
          />
        ),
        id: 'LocationId',
        disableSortBy: true,
        minWidth: 200,
        maxWidth: 350,
        omit: !isMultiLocation,
        accessor: (row) => row,
        cellRenderer: (row) => {
          const isColumnEditing = editingColId === 'LocationId';
          const isRowEditing = editingRowId === row.id;
          const isParentLocationId = isMultiLocation && parentLocationId && row.locationId === parentLocationId;
          const locationName = isParentLocationId ? t('Unassigned') : getLocationName(row.locationId);

          if (isColumnEditing) {
            const columnEditData = editingColData?.editingData.get(row.id);
            return (
              <EditCell
                colId='LocationId'
                editingRowData={columnEditData as EditingRowState}
                setEditingRowData={(updatedData) => {
                  if (typeof updatedData === 'function') return;
                  if (updatedData?.locationId) {
                    updateColumnEditData(row.id, 'locationId', updatedData.locationId);
                  }
                }}
                locationOptions={locationOptions}
                parentLocationId={effectiveParentLocationId}
              />
            );
          }

          if (isRowEditing) {
            return (
              <EditCell
                colId={'LocationId'}
                editingRowData={editingRowData}
                setEditingRowData={setEditingRowData}
                locationOptions={locationOptions}
                parentLocationId={effectiveParentLocationId}
              />
            );
          }
          return <Chip.Location maxWidth='100%'>{locationName}</Chip.Location>;
        },
      },
      {
        Header: <TableHeader colId='SourceId' />,
        id: 'SourceId',
        disableSortBy: true,
        minWidth: 150,
        omit: !showSourceColumn,
        accessor: (row) => row,
        cellRenderer: (row) => dataSourcesData.find(({ sourceId }) => sourceId === row.sourceId)?.sourceName || '-',
      },
      {
        Header: t('Display Status'),
        id: 'Hidden',
        disableSortBy: true,
        maxWidth: 150,
        accessor: (row) => row,
        cellRenderer: (row: Operatory) =>
          editingRowId === row.id ? (
            <EditCell
              colId='Hidden'
              editingRowData={editingRowData}
              setEditingRowData={setEditingRowData}
              parentLocationId={effectiveParentLocationId}
            />
          ) : (
            <>
              {row.hidden ? (
                <Chip leftElement={<Icon name='view-off-small' />} variant='neutral'>
                  {t('Hidden')}
                </Chip>
              ) : (
                <Chip leftElement={<Icon name='view-small' />}>{t('Visible')}</Chip>
              )}
            </>
          ),
      },
    ],
    [
      dataSourcesData,
      editingColData?.editingData,
      editingColId,
      editingRowData,
      editingRowId,
      getLocationName,
      handleCancelColumnEdit,
      handleColumnEdit,
      handleSaveDisplayNamesColumnEdit,
      handleSaveLocationsColumnEdit,
      isMultiLocation,
      locationOptions,
      effectiveParentLocationId,
      showSourceColumn,
      t,
      updateColumnEditData,
    ]
  );

  const tableActionMenu: TableRowActions<Operatory> = useMemo(
    () => ({
      actions: [
        {
          Icon: () => <InlineEditAction onSave={handleSaveRowEdit} onCancel={handleCancelRowEdit} />,
          label: t('Save Changes'),
          hide: (row: Operatory) => !row.details?.act || editingRowId !== row.id || !!editingColId,
        },
        {
          Icon: () => <Icon name='edit-small' />,
          label: t('Edit'),
          hide: (row: Operatory) => !row.details?.act || editingRowId === row.id || !!editingColId,
          onClick: handleRowEdit,
        },
        {
          Icon: () => <Icon name='archive-small' />,
          label: (row: Operatory) => `${!!row.details?.act ? t('Archive') : t('Unarchive')}`,
          hide: (row: Operatory) => isIntegratedOffice || editingRowId === row.id || !!editingColId,
          onClick: handleOnArchive,
        },
      ],
    }),
    [
      editingRowId,
      editingColId,
      handleCancelRowEdit,
      handleOnArchive,
      handleSaveRowEdit,
      handleRowEdit,
      isIntegratedOffice,
      t,
    ]
  );

  return (
    <Table
      isLoading={isLoading || updateOperatoryMutation.isLoading}
      colConfig={tableColumns}
      rowActions={tableActionMenu}
      tableStyle={{
        name: 'manage-operatory-v3-table',
        styles: `margin-bottom: ${theme.spacing(4)};`,
      }}
      data={operatoriesData || []}
      isPaginated
      hasResponsiveColWidths
      hasGlobalSearch
      globalSearchConfig={{
        initialValue: '',
        placeholder: t('Search Operatory'),
        searchHandler: (searchValue) => {
          if (totalCount) {
            setPageConfig((prev) => ({ ...prev, page: 1, searchValue }));
          }
        },
      }}
      onSortChange={(res) => {
        if (!res.length) {
          setPageConfig((prev) => ({ ...prev, page: 1, sortBy: undefined, sortDir: undefined }));
          return;
        }
        const { id, value } = res[0] ?? {};
        if (id && isOpertoriesV3SortByAvailable(id)) {
          const order = OPERTORIES_V3_SORT_BY[id];
          const sortDir = value === 'asc' ? SortOrder.ASC : SortOrder.DESC;
          setPageConfig((prev) => ({ ...prev, page: 1, sortBy: order, sortDir }));
        }
      }}
      tableActions={[
        {
          Icon: () => <Icon name='update' />,
          label: t('Refresh'),
          onClick: refetchOperatoryData,
          type: 'icon',
        },
      ]}
      styleConfig={{
        rows: (row: Operatory) => {
          if (row.details?.act === null || !row.details?.act) {
            return css`
              color: ${theme.font.colors.disabled};
              min-height: ${theme.spacing(7.2)};
              p {
                color: ${theme.font.colors.disabled};
              }
            `;
          }
          return css`
            min-height: ${theme.spacing(7.2)};
          `;
        },
      }}
      manualPaginationConfig={{
        showNumRowSelector: true,
        showGoToPageField: true,
        page: pageConfig.page || 1,
        rowsPerPageOptions: [10, 20, 30, 40, 50],
        defaultRowsPerPage: pageConfig.limit,
        hasNext: (pageConfig.page || 1) < Math.ceil((totalCount ?? 0) / pageConfig.limit),
        hasPrevious: pageConfig.page > 1,
        total: Math.ceil((totalCount ?? 0) / pageConfig.limit),
        onNumRowsChange: (pageSize) => {
          setPageConfig((prev) => ({ ...prev, limit: pageSize, page: 1 }));
        },
        handleChange: (pageNumber: number) => {
          setPageConfig((prev) => ({
            ...prev,
            page: pageNumber,
          }));
        },
      }}
      customToolbarRender={() => {
        return isMultiLocation ? (
          <TableCustomFilters dataSourcesData={dataSourcesData || []} setFilters={setFilters} />
        ) : null;
      }}
    />
  );
};
